import { createFileRoute } from '@tanstack/react-router';
import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { Anchor, Compass, Gauge, MapPin, Search, Ship, ChevronDown, ChevronRight, Cloud, Wind, Droplets, AlertCircle, Pencil, Trash2, Power, PowerOff, Radio } from 'lucide-react';
import DashboardLayout from '@/components/layouts/dashboard';
import useVesselsStore from '@/store/vessels';
import useAuthStore from '@/store/auth';
import { Vessel } from '@/types/api';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import Modal from '@/components/ui/modal';
import VesselForm from '@/featured/dashboard/vessels/vessel-form';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function Panel({
  title,
  action,
  children,
  className,
  description,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm", className)}>
      <div className="flex items-start justify-between p-5 pb-3">
        <div>
          <h3 className="font-semibold text-gray-900 dark:text-white tracking-tight">{title}</h3>
          {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      <div className="px-5 pb-5">{children}</div>
    </div>
  );
}

function StatusPill({
  tone,
  children,
}: {
  tone: "info" | "success" | "warning" | "danger" | "muted";
  children: React.ReactNode;
}) {
  const map = {
    info: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
    success: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
    warning: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400",
    danger: "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400",
    muted: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  } as const;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full", map[tone])}>
      <span className={cn("h-1.5 w-1.5 rounded-full", {
        info: "bg-blue-500",
        success: "bg-green-500",
        warning: "bg-yellow-500",
        danger: "bg-red-500",
        muted: "bg-gray-500",
      }[tone])} />
      {children}
    </span>
  );
}

function MiniStat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Ship;
  label: string;
  value: number;
  tone: "primary" | "success" | "warning" | "info";
}) {
  const toneMap = {
    primary: "bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400",
    success: "bg-green-50 text-green-600 dark:bg-green-500/10 dark:text-green-400",
    warning: "bg-yellow-50 text-yellow-600 dark:bg-yellow-500/10 dark:text-yellow-400",
    info: "bg-indigo-50 text-indigo-600 dark:bg-indigo-500/10 dark:text-indigo-400",
  };
  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-4 shadow-sm flex items-center gap-3">
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center", toneMap[tone])}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
        <div className="text-xl font-semibold tabular-nums text-gray-900 dark:text-white">{value}</div>
      </div>
    </div>
  );
}

function notifTone(vessel: Vessel) {
  if (vessel.is_off) return "danger" as const;
  return "success" as const;
}

export default function DashboardVessels() {
  const vessels = useVesselsStore((state) => state.vessels);
  const user = useAuthStore((state) => state.user);
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const [modalForm, setOpenModalForm] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState<Vessel | null>(null);

  const toggleExpand = (id: string) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return vessels;
    return vessels.filter(
      (v) =>
        v.vessel_name.toLowerCase().includes(t) ||
        v.id.toLowerCase().includes(t) ||
        (v.device_type && v.device_type.toLowerCase().includes(t)) ||
        (v.ship_type && v.ship_type.toLowerCase().includes(t))
    );
  }, [q, vessels]);

  const active = vessels.filter((v) => parseFloat(v.speed || "0") > 0).length;
  const online = vessels.filter((v) => !v.is_off).length;
  const idle = vessels.length - active;
  const offline = vessels.length - online;

  return (
    <div className="w-full space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">All Vessels</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Live registry of every vessel reporting to FleetOps.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat icon={Ship} label="Total fleet" value={vessels.length} tone="primary" />
        <MiniStat icon={Gauge} label="Underway" value={active} tone="success" />
        <MiniStat icon={Anchor} label="Idle / berthed" value={idle} tone="warning" />
        <MiniStat icon={MapPin} label="Offline" value={offline} tone="info" />
      </div>

      <Panel
        title={`Vessel list (${filtered.length})`}
        description="Search by name, IMO/ID, or type"
        action={
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search vessels…"
              className="h-9 w-64 pl-8 pr-3 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 text-gray-900 dark:text-white"
            />
          </div>
        }
      >
        {/* Desktop table — hidden on mobile */}
        <div className="hidden md:block overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800">
                <th className="py-2 pr-3 font-medium">Vessel</th>
                <th className="py-2 pr-3 font-medium">Type</th>
                <th className="py-2 pr-3 font-medium">Position</th>
                <th className="py-2 pr-3 font-medium">Speed</th>
                <th className="py-2 pr-3 font-medium">Heading</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">Last update</th>
                <th className="py-2 pr-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => {
                const history = v.vessel_history?.[0];
                const weather = history?.vessel_history_meteorology;
                const notifs = history?.notifications || [];
                const isExpanded = !!expanded[v.id];
                const isBlocked = user && (v.customer_id !== user.id || !!v.renter_id);

                return (
                  <React.Fragment key={v.id}>
                    <tr
                      className={cn(
                        "border-b border-gray-200/60 dark:border-gray-800/60 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer",
                        isExpanded && "bg-gray-50/30 dark:bg-gray-800/30"
                      )}
                      onClick={() => toggleExpand(v.id)}
                    >
                      <td className="py-3 pr-3 pl-2 flex items-start gap-2">
                        <div className="mt-1">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{v.vessel_name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{v.id} · {v.device_type}</div>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-gray-500 dark:text-gray-400 capitalize">{v.ship_type || v.device_type || '-'}</td>
                      <td className="py-3 pr-3 text-gray-500 dark:text-gray-400 tabular-nums">
                        {history?.proximity ? (
                          <div className="truncate max-w-[150px]" title={history.proximity}>
                            {history.proximity}
                          </div>
                        ) : (
                          <span>{Number(v.latitude).toFixed(3)}, {Number(v.longitude).toFixed(3)}</span>
                        )}
                      </td>
                      <td className="py-3 pr-3 tabular-nums text-gray-900 dark:text-gray-300">{v.speed || '0'} kn</td>
                      <td className="py-3 pr-3 tabular-nums inline-flex items-center gap-1 text-gray-900 dark:text-gray-300">
                        <Compass className="h-3.5 w-3.5 text-gray-500" />
                        {v.heading || '0'}°
                      </td>
                      <td className="py-3 pr-3">
                        <div className="flex flex-col gap-1 items-start">
                          <StatusPill tone={notifTone(v)}>
                            {v.is_off ? "Offline" : "Online"}
                          </StatusPill>
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {v.latest_receive_date}
                      </td>
                      <td className="py-3 pr-3 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedVessel(v);
                              setOpenModalForm(true);
                            }}
                            className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/20 rounded transition-colors"
                            title="Edit Vessel"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="border-b border-gray-200/60 dark:border-gray-800/60 bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={7} className="py-4 px-6 border-l-4 border-brand-500">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                            {/* Location Details */}
                            <div className="space-y-2">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1"><MapPin className="h-3 w-3" /> Location Details</h4>
                              <div className="text-sm">
                                <div className="text-gray-900 dark:text-gray-100 font-medium">{history?.proximity || "Unknown Location"}</div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                                  {history?.proximity_distance ? `${history.proximity_distance} nm` : ""} {history?.proximity_direction ? `to ${history.proximity_direction}` : ""}
                                </div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs mt-1 font-mono">
                                  {v.latitude}, {v.longitude}
                                </div>
                              </div>
                            </div>

                            {/* Weather */}
                            <div className="space-y-2">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1"><Cloud className="h-3 w-3" /> Weather</h4>
                              {weather ? (
                                <div className="flex flex-col gap-2 text-sm">
                                  <div className="flex items-center gap-3">
                                    <span className="text-2xl font-light text-gray-900 dark:text-gray-100">{weather.temp}°C</span>
                                    <div className="flex flex-col">
                                      <span className="capitalize text-gray-900 dark:text-gray-100 font-medium">{weather.summary}</span>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">Feels like {weather.feels_like}°C</span>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 mt-2 gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center gap-1"><Wind className="h-3 w-3" /> {weather.wind_speed} m/s, {weather.wind_deg}°</div>
                                    <div className="flex items-center gap-1"><Droplets className="h-3 w-3" /> {weather.humidity}% humidity</div>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 dark:text-gray-400 italic">No weather data</div>
                              )}
                            </div>

                            {/* Notifications */}
                            <div className="space-y-2">
                              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3 flex items-center gap-1"><AlertCircle className="h-3 w-3" /> Recent Alerts</h4>
                              {notifs.length > 0 ? (
                                <div className="space-y-3 max-h-32 overflow-y-auto pr-2 custom-scrollbar">
                                  {notifs.map(n => (
                                    <div key={n.id} className="text-xs border-l-2 border-brand-500 pl-2.5 py-0.5">
                                      <div className="font-medium text-gray-900 dark:text-gray-100 capitalize">{n.alert_type}</div>
                                      <div className="text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5" title={n.message}>{n.message}</div>
                                      <div className="text-gray-400 dark:text-gray-500 text-[10px] mt-1">{n.created_at}</div>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="text-sm text-gray-500 dark:text-gray-400 italic">No recent alerts</div>
                              )}
                            </div>

                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-10 text-center text-gray-500 dark:text-gray-400">
                    No vessels match &quot;{q}&quot;.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile card list — visible only on small screens */}
        <div className="md:hidden -mx-5 px-5 space-y-3">
          {filtered.map((v) => (
            <MobileVesselCard
              key={v.id}
              vessel={v}
              onEdit={() => {
                setSelectedVessel(v);
                setOpenModalForm(true);
              }}
            />
          ))}
          {filtered.length === 0 && (
            <div className="py-10 text-center text-sm text-gray-500 dark:text-gray-400">
              No vessels match &quot;{q}&quot;.
            </div>
          )}
        </div>
      </Panel>

      <Modal modal={modalForm} setModal={setOpenModalForm} className="max-w-3xl max-h-full">
        <VesselForm
          vesselData={selectedVessel}
          closeForm={() => {
            setSelectedVessel(null);
            setOpenModalForm(false);
          }}
        />
      </Modal>
    </div>
  );
}

DashboardVessels.layout = DashboardLayout as React.FC<{
  children: React.ReactNode;
}> as any;

function MobileVesselCard({ vessel, onEdit }: { vessel: Vessel; onEdit: () => void }) {
  const isOff = vessel.is_off;
  const statusMeta = isOff
    ? { label: 'Offline', tone: 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800', Icon: PowerOff }
    : { label: 'Online', tone: 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800', Icon: Power };
  const StatusIcon = statusMeta.Icon;

  const history = vessel.vessel_history?.[0];
  const weather = history?.vessel_history_meteorology;
  const notifs = history?.notifications || [];

  return (
    <div
      className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 shadow-sm cursor-pointer hover:border-brand-500 transition-colors"
      onClick={onEdit}
    >
      {/* Name row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
            <span className="capitalize">{vessel.device_type}</span>
            {vessel.ship_type && (
              <>
                <span>·</span>
                <span className="capitalize">{vessel.ship_type}</span>
              </>
            )}
          </div>
          <div className="text-sm font-semibold tracking-tight truncate mt-0.5 text-gray-900 dark:text-white">{vessel.vessel_name}</div>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">ID {vessel.id}</div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <span
            className={cn(
              'inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] font-medium border',
              statusMeta.tone
            )}
          >
            <StatusIcon className="h-3 w-3" />
            {statusMeta.label}
          </span>
        </div>
      </div>

      {/* Telemetry grid */}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <MapPin className="h-3 w-3" />
            Position
          </div>
          <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-gray-200 truncate">
            {Number(vessel.latitude || 0).toFixed(3)}, {Number(vessel.longitude || 0).toFixed(3)}
          </div>
        </div>
        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Gauge className="h-3 w-3" />
            Speed
          </div>
          <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-gray-200">{Number(vessel.speed || 0).toFixed(1)} kn</div>
        </div>
        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Compass className="h-3 w-3" />
            Heading
          </div>
          <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-gray-200">{Number(vessel.heading || 0).toFixed(0)}°</div>
        </div>
        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/50 p-2.5">
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Radio className="h-3 w-3" />
            Updated
          </div>
          <div className="text-xs font-semibold mt-1 tabular-nums text-gray-900 dark:text-gray-200 truncate">{vessel.latest_receive_date}</div>
        </div>
      </div>

      {/* Additional Details: Location, Weather, Alerts */}
      {(history?.proximity || weather || notifs.length > 0) && (
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 space-y-3">
          {/* Location */}
          {history?.proximity && (
            <div className="flex items-start gap-2 text-xs">
              <MapPin className="h-3.5 w-3.5 text-brand-500 mt-0.5 shrink-0" />
              <div>
                <div className="text-gray-900 dark:text-gray-100 font-medium">{history.proximity}</div>
                {(history.proximity_distance || history.proximity_direction) && (
                  <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                    {history.proximity_distance ? `${history.proximity_distance} nm` : ""} {history.proximity_direction ? `to ${history.proximity_direction}` : ""}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Weather */}
          {weather && (
            <div className="flex items-start gap-2 text-xs">
              <Cloud className="h-3.5 w-3.5 text-blue-400 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-gray-900 dark:text-gray-100 font-medium">{weather.temp}°C</span>
                  <span className="capitalize text-gray-700 dark:text-gray-300">{weather.summary}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400 mt-0.5">
                  <div className="flex items-center gap-1"><Wind className="h-3 w-3" /> {weather.wind_speed} m/s</div>
                  <div className="flex items-center gap-1"><Droplets className="h-3 w-3" /> {weather.humidity}%</div>
                </div>
              </div>
            </div>
          )}

          {/* Alerts */}
          {notifs.length > 0 && (
            <div className="flex items-start gap-2 text-xs">
              <AlertCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">Recent Alerts ({notifs.length})</div>
                <div className="space-y-2">
                  {notifs.slice(0, 2).map((n) => (
                    <div key={n.id} className="border-l-2 border-red-500 pl-2">
                      <div className="font-medium text-gray-800 dark:text-gray-200 capitalize">{n.alert_type}</div>
                      <div className="text-gray-500 dark:text-gray-400 line-clamp-1" title={n.message}>{n.message}</div>
                    </div>
                  ))}
                  {notifs.length > 2 && (
                    <div className="text-gray-400 dark:text-gray-500 text-[10px] italic">+{notifs.length - 2} more alerts</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/dashboard/vessels')({
  component: DashboardVessels,
});
